import React from 'react';
import { PortfolioAnalysisItem } from 'components/features/target-achievement-support/utils/type';
import {
  portfolioTypeToBackgroundColor,
  portfolioTypeToString,
} from 'components/features/target-achievement-support/utils/portfolioInfo';
import { commaFormat } from 'components/features/new-report/utils/formatter';

const PortfolioItem = ({
  onClick,
  portfolio,
}: {
  portfolio: PortfolioAnalysisItem;
  onClick: () => void;
}): React.ReactElement => {
  const padStartTransparentZero = (
    ...param: Parameters<typeof commaFormat>
  ): React.ReactElement => {
    const result = commaFormat(...param) as string;
    if (result.length === 1)
      return (
        <>
          <div className="flex flex-row">
            <div className="text-transparent">0</div>
            {result}
          </div>
        </>
      );
    return <>{result}</>;
  };

  return (
    <>
      <div
        className={`grid rounded-lg border shadow ${
          portfolio.currentPer >= portfolio.targetPer
            ? 'border-bluegray-100'
            : 'border-teal-500'
        } ${portfolio.assetType !== 'OTHER' ? 'cursor-pointer' : ''}`}
        style={{ gridTemplateColumns: '1fr auto' }}
        onClick={portfolio.assetType === 'OTHER' ? undefined : onClick}>
        <div className="flex flex-row items-center justify-between pl-3 pr-6 py-3">
          <div className="flex flex-row items-center gap-2">
            <div
              className="rounded-full h-3 w-3"
              style={{
                backgroundColor: portfolioTypeToBackgroundColor(
                  portfolio.assetType
                ),
              }}></div>
            <div className="text-sm font-bold">
              {portfolioTypeToString(portfolio.assetType)}
            </div>
          </div>
          <div
            className="grid"
            style={{ gridTemplateColumns: 'auto auto auto' }}>
            <div
              className="text-xs text-bluegray-700 flex justify-center items-center"
              style={{ gridColumn: '1', gridRow: '1' }}>
              現在
            </div>
            <div
              className="text-xs text-bluegray-700 flex items-center justify-center"
              style={{ gridColumn: '3', gridRow: '1' }}>
              目標
            </div>
            <div
              className="flex flex-row items-baseline mt-2 leading-4"
              style={{ placeSelf: 'center', gridRow: '2', gridColumn: '1' }}>
              <div className="text-2xl">
                {padStartTransparentZero(portfolio.currentPer, {
                  numberOfDecimals: 0,
                })}
              </div>
              <div className="text-xs">%</div>
            </div>
            <div
              className="icon-chevrons-right mr-2 ml-3 flex flex-row items-center text-2xl text-bluegray-300 justify-center leading-4"
              style={{
                placeSelf: 'center',
                gridColumn: '2',
                gridRow: '2',
              }}></div>
            <div
              className="flex flex-row items-baseline mt-2 leading-4"
              style={{ placeSelf: 'center', gridColumn: '3', gridRow: '2' }}>
              <div
                className={`text-2xl ${
                  portfolio.currentPer >= portfolio.targetPer
                    ? 'text-gray-500'
                    : 'text-teal-500'
                }`}>
                {padStartTransparentZero(portfolio.targetPer, {
                  numberOfDecimals: 0,
                })}
              </div>
              <div className={`text-xs`}>%</div>
            </div>
          </div>
        </div>
        {portfolio.assetType === 'OTHER' ? (
          <div className="w-8 px-1"></div>
        ) : (
          <div className="icon-chevron-right text-2xl font-bold text-bluegray-400 flex flex-row justify-center items-center bg-bluegray-60 px-1 rounded-r-lg"></div>
        )}
      </div>
    </>
  );
};

type Props = {
  portfolios: PortfolioAnalysisItem[];
  goToIssueDetail: (portfolio: PortfolioAnalysisItem) => void;
  className?: string;
};

export const PortfolioItemList = ({
  portfolios,
  goToIssueDetail,
  className,
}: Props): React.ReactElement => {
  return (
    <>
      <div
        className={`flex flex-col gap-2 bg-white ${className ?? ''}`}
        style={{
          gridTemplateColumns: '1fr auto auto auto auto',
        }}>
        {portfolios.map((portfolio, i) => {
          return (
            <PortfolioItem
              {...{
                portfolio,
                onClick: () => goToIssueDetail(portfolio),
              }}
              key={i}
            />
          );
        })}
      </div>
    </>
  );
};
